import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import { SoftwareWizardEmployeeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getPhoneCode } from 'modules/expenses/lib/countryCodes'

import { EmployeesStep } from './EmployeesStep'
import { SoftwareStep } from './SoftwareStep'
import { SpendControlStep } from './SpendControlStep'
import { WizardData } from './types'

interface Props {
  onSuccess: (setupId: number) => void
  onClose: () => void
  employee: SoftwareWizardEmployeeFragment
}

const SoftwareWizard = ({ onSuccess, onClose, employee }: Props) => {
  const messages = {
    error: i18n.t('it_management.initial_setup.alert.errorMessage'),
    success: i18n.t('it_management.initial_setup.alert.successMessage'),
  }
  const currency = employee?.company?.currency || employee?.bankingCardholder?.currency || ''
  if (!employee) {
    return null
  }

  const initialData: WizardData = {
    currency: currency as Currency,
    employeeId: employee.id,
    birthDate: employee.birthdayOn,
    phoneNumber: employee.phoneNumber,
    countryCode: employee.phoneCode || getPhoneCode(employee.country) || '',
    budgetAmount: '1000',
    spendingPeriod: 'monthly',
    employees: [],
    spendType: '',
    employee: employee,
  }

  const handleSubmit = (data: WizardData) => {
    if (data.success) {
      app.setFlash({ type: 'correct', message: messages.success })

      onSuccess(data.setupId!)
    } else {
      let message = messages.error

      if (data.errors) {
        message = Object.values(data.errors).join(', ')
      }
      app.setFlash({ type: 'wrong', message: message })
    }
  }

  const employeesStep: WizardStep<WizardData> = {
    title: i18n.t('it_management.initial_setup.wizard.steps.employees.title'),
    nextText: i18n.t('common.finish'),
    render: (props) => <EmployeesStep {...props} />,
    isCompleted: (data) => !!data.employees,
  }

  const softwareStep: WizardStep<WizardData> = {
    title: i18n.t('it_management.initial_setup.wizard.steps.software.title'),
    nextText: i18n.t('common.next'),
    render: (props) => <SoftwareStep {...props} />,
    isCompleted: (data) => !!(data.softwareId || data.softwareName),
  }

  const spendControlStep: WizardStep<WizardData> = {
    title: i18n.t('it_management.initial_setup.wizard.steps.spend_control.title'),
    nextText: i18n.t('common.next'),
    render: (props) => <SpendControlStep {...props} />,
    isCompleted: (data) => {
      const cardholder = data.employee.bankingCardholder
      const cardholderExists = !!cardholder
      if (data.spendType === 'card') {
        if (cardholderExists) {
          return !!(data.budgetAmount && data.spendingAmount && data.spendingPeriod)
        } else {
          return !!(
            data.budgetAmount &&
            data.spendingAmount &&
            data.spendingPeriod &&
            data.birthDate &&
            data.countryCode &&
            data.phoneNumber
          )
        }
      } else if (data.spendType === 'manual') {
        return true
      } else {
        return false
      }
    },
  }

  const steps: WizardStep<WizardData>[] = [softwareStep, spendControlStep, employeesStep]

  return (
    <Wizard.Modal
      title={i18n.t('it_management.initial_setup.wizard.steps.software.title')}
      icon="sync"
      steps={steps}
      initialData={initialData}
      onSubmit={(data) => {
        handleSubmit(data)
      }}
      onClose={onClose}
    />
  )
}

export default SoftwareWizard
